import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Play, X, ChevronsLeftRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const SERVICES = [
  "Custom speaker installation",
  "Subwoofer builds & enclosures",
  "Amplifier installation",
  "Full system upgrades",
  "Clean wire management",
  "Sound tuning & calibration",
  "OEM integration",
];

function BeforeAfter() {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-white/10 select-none cursor-ew-resize touch-none"
      onPointerDown={(e) => {
        dragging.current = true;
        ref.current.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
    >
      <img src="/assets/before-trunk.jpg" alt="Trunk before build" className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <img src="/assets/after-subwall.jpg" alt="Trunk after custom subwoofer wall build" className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      </div>
      <span className="absolute top-4 left-4 rounded-full bg-black/60 px-3 py-1 font-tech text-[10px] uppercase tracking-[0.25em] text-white/80 backdrop-blur">
        Before
      </span>
      <span className="absolute top-4 right-4 rounded-full bg-[#FF5500] px-3 py-1 font-tech text-[10px] uppercase tracking-[0.25em] text-white">
        After
      </span>
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -ml-px w-0.5 bg-[#FF5500]" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-[#FF5500] text-white volt-glow">
          <ChevronsLeftRight size={18} />
        </div>
      </div>
    </div>
  );
}

function VideoModal({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
          onClick={onClose}
          data-testid="video-modal"
        >
          <motion.div
            initial={{ scale: 0.92, y: 24 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.92, y: 24 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="video-modal-close"
              onClick={onClose}
              className="absolute -top-12 right-0 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white hover:border-[#FF5500] hover:text-[#FF5500] transition-colors"
              aria-label="Close video"
            >
              <X size={18} />
            </button>
            <video
              src="/assets/demo.mp4"
              poster="/assets/demo-poster.jpg"
              controls
              autoPlay
              playsInline
              className="w-full rounded-xl border border-white/10"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function AudioSection() {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <section id="audio" className="relative py-24 sm:py-36" data-testid="audio-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <Eyebrow>Section 01 — Audio</Eyebrow>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                Precision Car Audio
                <span className="block text-[#FF5500]">Installation</span>
              </h2>
              <p className="mt-6 text-base sm:text-lg text-white/70 leading-relaxed">
                It's not just about being loud. It's about being clean. Professional-grade
                audio installs designed for clarity, depth, and power — from subtle
                factory-level upgrades to full custom builds that shake the block.
              </p>
              <p className="mt-4 text-sm text-white/50 leading-relaxed">
                No messy wiring. No shortcuts. No "good enough." Every system is built
                like it's going in our own ride.
              </p>
            </Reveal>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              {SERVICES.map((s, i) => (
                <Reveal key={s} delay={i * 0.05}>
                  <div
                    data-testid={`audio-service-${i}`}
                    className="group flex items-center gap-3 border-b border-white/10 py-3.5"
                  >
                    <Plus
                      size={15}
                      className="shrink-0 text-[#FF5500] transition-transform duration-300 group-hover:rotate-90"
                    />
                    <span className="text-sm text-white/80 group-hover:text-white transition-colors duration-300">
                      {s}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <Reveal delay={0.1}>
              <BeforeAfter />
              <p className="mt-3 font-tech text-[10px] uppercase tracking-[0.25em] text-white/40">
                Drag to compare — gutted trunk to full subwoofer wall
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <button
                data-testid="video-open-button"
                onClick={() => setVideoOpen(true)}
                className="group relative block w-full overflow-hidden rounded-2xl border border-white/10"
              >
                <img
                  src="/assets/demo-poster.jpg"
                  alt="Audio system demo video"
                  className="aspect-video w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/40 transition-colors duration-500 group-hover:bg-black/25" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#FF5500] text-white volt-glow transition-transform duration-300 group-hover:scale-110">
                    <Play size={22} className="ml-1" fill="currentColor" />
                  </span>
                </div>
                <span className="absolute bottom-4 left-4 font-tech text-[10px] uppercase tracking-[0.25em] text-white/80">
                  Watch a system in action
                </span>
              </button>
            </Reveal>
            <div className="grid grid-cols-2 gap-4">
              <Reveal delay={0.25} className="h-full">
                <img
                  src="/assets/audio-m1-subs.jpg"
                  alt="Dual subwoofer enclosure with illuminated M1 acrylic"
                  className="h-48 w-full rounded-2xl border border-white/10 object-cover sm:h-56"
                />
              </Reveal>
              <Reveal delay={0.3} className="h-full">
                <img
                  src="/assets/audio-door-build.jpg"
                  alt="Custom door panel speaker and amplifier build"
                  className="h-48 w-full rounded-2xl border border-white/10 object-cover sm:h-56"
                />
              </Reveal>
            </div>
          </div>
        </div>
      </div>
      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />
    </section>
  );
}
